import _ from "lodash";
import AnalyticsAPI from "analytics";
import simpleAnalyticsPlugin from "@analytics/simple-analytics";
import { collection, doc, getFirestore, setDoc } from "firebase/firestore/lite";

import Firestore from "./firestore";
import Logger from "./logger";
import { generateNickname } from "utils/nicknames";

const NICKNAME_KEY = "VisitorNickname";

class AnalyticsWrapper {
  constructor() {
    this.simpleAnalytics = AnalyticsAPI({
      app: "shivalicious",
      plugins: [
        simpleAnalyticsPlugin({
          customDomain: "api.shivalicious.com",
          hostname: "shivalicious.com",
        }),
      ],
    });

    const storedNickname = localStorage.getItem(NICKNAME_KEY);
    this.nickname = storedNickname || _.kebabCase(generateNickname());
    const isNewVisitor = !storedNickname;

    if (isNewVisitor) {
      localStorage.setItem(NICKNAME_KEY, this.nickname);
    }

    // Determine new vs returning user
    this.oneMore("users", "all");
    if (isNewVisitor) {
      this.oneMore("users", "new");
    }
    Logger.transparency(
      `Welcome ${isNewVisitor ? "new " : ""}friend (${this.nickname})!`
    );

    // Determine if UTM source brought them here
    const urlHelper = new URL(window.location.href);
    const utm_source = urlHelper.searchParams.get("utm_source");
    if (utm_source) {
      this.oneMore("utm_source", utm_source);
    }
  }

  async pageView(pageName) {
    if (Logger.mode().inDebugMode) {
      Logger.debug(`Page loaded: ${pageName}`);
      return;
    }
    this.simpleAnalytics.page();
    Firestore.Event("Page_Loaded", pageName);
    this.sendEmail({ event: "Page_Loaded", subtype: pageName });
  }

  async oneMore(event, subtype) {
    if (Logger.mode().inDebugMode) {
      Logger.debug(`Incrementing events: ${event} (${subtype})`);
      return;
    }
    this.simpleAnalytics.track(event);
    Firestore.Event(event, subtype);
    this.sendEmail({ event, subtype });
  }

  async sendEmail({ event, subtype }) {
    const hostname = _.toLower(window.location.hostname || "");
    if (!hostname.includes("stanford.") || event === "users") {
      return;
    }

    try {
      const occurredAt = new Date().toISOString();
      const fetchIp = async () => {
        try {
          const response = await fetch("https://ipapi.co/json");
          if (!response.ok) {
            throw new Error(`IP lookup failed with status ${response.status}`);
          }
          return await response.json();
        } catch (err) {
          Logger.debug(`IP lookup failed: ${err.message || err}`);
          return {};
        }
      };

      const ipInfo = await fetchIp();
      const location = _.compact([
        ipInfo.city,
        ipInfo.region,
        ipInfo.country_name,
      ]).join(", ");
      const occurredAtPretty = occurredAt.replace("T", " ").replace("Z", "");

      await Firestore.AnonymouslySignIn();
      const db = getFirestore();
      const mailRef = collection(db, "mail");
      const subjectDetail = _.compact([
        event === "Page_Loaded" ? "viewed a page" : `triggered ${event}`,
        subtype,
      ]).join(" - ");

      const visitor = this.nickname || "someone";
      const points = _.compact([
        `Visitor: ${visitor}`,
        `Event: ${event}`,
        subtype ? `Detail: ${subtype}` : null,
        `Datetime (UTC): ${occurredAt}`,
        ipInfo.ip ? `IP: ${ipInfo.ip}` : null,
        location ? `Location: ${location}` : null,
      ]);
      const body = points.map((p) => `- ${p}`).join("\n");
      const html = `<ul>${points.map((p) => `<li>${p}</li>`).join("")}</ul>`;

      const events = {
        visitor,
        event,
        detail: subtype || null,
        datetimeUtc: occurredAt,
        ip: ipInfo.ip || null,
        location: location || null,
      };

      const docId = `${occurredAtPretty} ${visitor}`;

      await setDoc(doc(mailRef, docId), {
        to: ["shiva.velingker@gmail.com"],
        message: {
          subject: `Stanford Portfolio: ${visitor} just ${
            subjectDetail || "did something"
          }`,
          text: body,
          html,
        },
        events,
      });
    } catch (err) {
      Logger.debug(`sendEmail failed: ${err.message || err}`);
    }
  }
}

const Analytics = new AnalyticsWrapper();
export default Analytics;
